import React from 'react';
import { array, bool, func, object, string } from 'prop-types';
import { I18n as I } from 'react-redux-i18n';
import Portlet from '../layout/Portlet';
import {
  BaseButton as Button,
  FormField,
  isNil,
  FieldInfo,
  ControlTypes,
} from 'vdlib';
import { validate } from './utils';

const UpdateGarage = ({
  countries,
  embedded,
  garage,
  lang,
  onSubmit,
  regions,
  taxOffices,
}) => {
  const [formData, setFormData] = React.useState({
    data: {},
    validationMessages: {},
  });

  const formContext = React.useRef({ updateForm: () => {} });

  const fields = React.useMemo(
    () =>
      !garage
        ? []
        : [
            new FieldInfo(
              'DistinctiveTitle',
              I.t('TITLE'),
              garage.DistinctiveTitle
            ),
            new FieldInfo('AFM', I.t('TAXNUMBER'), garage.AFM),
            new FieldInfo(
              'TaxOfficeID',
              I.t('TAXOFFICE'),
              garage.TaxOfficeID,
              taxOffices,
              null,
              ControlTypes.Dropdown
            ),
            new FieldInfo(
              'CountryID',
              I.t('COUNTRY'),
              garage.CountryID,
              countries,
              null,
              ControlTypes.Dropdown
            ),
            new FieldInfo(
              'RegionID',
              I.t('REGION'),
              garage.RegionID,
              regions,
              null,
              ControlTypes.Dropdown
            ),
            new FieldInfo('City', I.t('CITY'), garage.City),
            new FieldInfo('Street', I.t('STREET'), garage.Street),
            new FieldInfo('StreetNumber', I.t('NUMBER'), garage.StreetNumber),
            new FieldInfo('PostalCode', I.t('POSTALCODE'), garage.PostalCode),
            new FieldInfo('Phone', I.t('PHONE'), garage.Phone),
            new FieldInfo('Fax', I.t('FAX'), garage.Fax),
            new FieldInfo('Email', I.t('EMAIL'), garage.Email),
            new FieldInfo('KAD', I.t('ACTIVΙΤΥ'), garage.KAD),
          ].map(f => ({ ...f, formContext: formContext.current })),
    [countries, garage, lang, regions, taxOffices] //eslint-disable-line
  );

  const handleUpdateForm = React.useCallback(
    (key, value) => {
      const validationMessage = validate(fields, key, value);

      setFormData(currentFormData => {
        const { data, validationMessages } = currentFormData;

        return {
          data: { ...data, [key]: value },
          validationMessages: {
            ...validationMessages,
            [key]:
              validationMessage === null ||
              isNil(validationMessage[key] ? null : validationMessage[key]),
          },
        };
      });
    },
    [fields, setFormData]
  );

  React.useEffect(() => {
    formContext.current.updateForm = handleUpdateForm;
  }, [handleUpdateForm]);

  const initialized = React.useRef(false);
  React.useEffect(() => {
    if (garage && fields && initialized.current !== true) {
      initialized.current = true;
      const dataObj = {};
      const validationMessagesObj = {};
      for (let field of fields) {
        dataObj[field.name] = field.defaultValue;
        validationMessagesObj[field.name] = '';
      }
      setFormData({
        data: dataObj,
        validationMessages: validationMessagesObj,
      });
    }
  }, [fields, garage, setFormData]);

  const handleSubmit = React.useCallback(
    e => {
      if (e && e.preventDefault) e.preventDefault();

      const validationResult = validate(fields, formData.data);
      const invalidKeys = Object.keys(validationResult);

      if (invalidKeys.length === 0)
        onSubmit({
          ...formData.data,
          ID: garage.ID,
        });
      else setFormData(fd => ({ ...fd, validationMessages: validationResult }));

      return false;
    },
    [fields, formData, garage, onSubmit]
  );

  return (
    <Portlet
      title={embedded === true ? null : I.t('GARAGE_EDIT_C')}
      icon={embedded === true ? null : 'fa fa-user-plus'}
      bodyClassName="form"
      className="col-md-12"
    >
      <form action="#" onSubmit={handleSubmit} className="form-horizontal">
        <div className="form-body clearfix">
          {embedded !== true && (
            <h3 className="form-section">{I.t('BASIC_INFO')}</h3>
          )}
          <div className="row">
            {fields.map(f => (
              <FormField
                key={f.name}
                field={f}
                value={formData.data[f.name]}
                validationMessage={formData.validationMessages[f.name]}
              />
            ))}
          </div>
        </div>
        {embedded !== true && (
          <div className="form-actions">
            <div className="row">
              <div className="col-md-12">
                <Button type="submit" className="btn btn-sm green float-right">
                  {I.t('SAVE')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </form>
    </Portlet>
  );
};

UpdateGarage.propTypes = {
  countries: array,
  embedded: bool,
  garage: object,
  lang: string,
  onSubmit: func,
  regions: array,
  taxOffices: array,
};

export default UpdateGarage;
